import { inject, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { AuthenticationStore } from '../states/auth/auth.store';
import { environment } from '../../../environments/environment';

const AUTH_URLS = [
  environment.user.auth,
  environment.user.register,
  environment.user.refresh,
  environment.user.logout,
  environment.user.validate,
];

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private readonly injector = inject(Injector);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(this.withAuthHeader(req)).pipe(
      catchError(err => {
        const isAuthEndpoint = AUTH_URLS.some(url => req.url.startsWith(url));
        if (err instanceof HttpErrorResponse && err.status === 401 && !isAuthEndpoint) {
          return this.handle401(req, next);
        }
        return throwError(() => err);
      }),
    );
  }

  private withAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    const token = localStorage.getItem(environment.api_token);
    if (!token) {
      return req.clone({ withCredentials: true });
    }

    return req.clone({
      withCredentials: true,
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private handle401(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authService = this.injector.get(AuthService);
    const authStore = this.injector.get(AuthenticationStore);

    if (authService.isLogoutInProgress()) {
      return throwError(() => new Error('Logout in progress'));
    }

    return from(authStore.refreshSession()).pipe(
      switchMap(() => {
        if (!authStore.isAuthenticated()) {
          return throwError(() => new Error('Session expired'));
        }
        return next.handle(this.withAuthHeader(request));
      }),
    );
  }
}
